"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useMemo } from "react";

/**
 * Key Numbers 5006 — KUK programme key features.
 *
 * Port of kuk-campus-portal `components/Degree/KeyFeatures.tsx`: the tinted strip
 * directly under the hero carrying the programme's headline facts — duration,
 * delivery mode, fee, elective count.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * COLUMN COUNT — v1 is a 2×2 grid below `tab` and a single row above it, with the
 * desktop column count narrowed to match the item count for one, two or three
 * items (four or more all land on four columns and wrap). Reproduced exactly,
 * including the wrap, rather than switching to `auto-fit`: a fifth item in v1
 * starts a second row of four, and `auto-fit` would instead squeeze five across.
 *
 * ICONS — v1 names its icons from a code-side map ('clock', 'monitor',
 * 'givingMoney', 'books') and renders them as inline SVG. This uses
 * `DynamicIcon`, which accepts BOTH an icon-map key and an uploaded image URL,
 * so the reference's names carry over verbatim AND an editor can upload custom
 * art. That is the one deliberate improvement on the reference here: the
 * existing Key Numbers variants take a URL only, which would have silently
 * dropped every icon on a migrated page.
 *
 * DIVIDERS — v1's rule pattern is `odd:border-r even:border-r-0 border-b` below
 * `tab` (so the 2×2 grid gets a centre column rule and a row rule) and
 * `border-x` with `last-of-type:border-r-0` above it. Kept as-is; the colour
 * moves to a CMS field.
 *
 * DROPPED — v1's `max-h-[300px]` on the container. With four items in a 2×2 grid
 * the content is taller than 300px on a narrow phone, so the class clips the
 * second row's text; the block here grows to fit its content instead.
 */

/**
 * v1's `container` is NOT this app's `container`. KUK's Tailwind config defines
 * it as `{ center: true, padding: "2rem", screens: { "2xl": "1400px" } }` — so
 * the reference row is capped at 1400px with 32px gutters. This app has no
 * container config, so in Tailwind v4 `.container` is `width:100%` with its
 * max-width tracking the breakpoint (2100px at `2xl`) and NO padding — which
 * renders the row nearly edge-to-edge and puts the column dividers in visibly
 * different places. Spelled out here so it cannot silently drift again.
 */
const CONTAINER = "mx-auto w-full max-w-[1400px] px-8";

/** v1: `width="35" height="35"` above `tab`, `25` below. */
const ICON_SIZE_DESKTOP = "35";
const ICON_SIZE_MOBILE = "25";

interface KeyFeatureRow {
  id?: string | number;
  /** An `iconMap` key (e.g. "clock") or an uploaded icon URL. */
  meta_icon?: string;
  meta_icon_alt?: string;
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface KeyNumbers5006Data {
  status?: boolean | number | string;
  /** Strip background. */
  background_color?: string;
  /** Feature text. */
  description_color?: string;
  /** Icon tint (icon-map icons only; uploaded art keeps its own colours). */
  gradient_color_1?: string;
  /** Divider rules between cells. */
  gradient_color_4?: string;
  metadata?: KeyFeatureRow[];
}

const KeyNumbers5006 = (props: { data?: KeyNumbers5006Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | KeyNumbers5006Data
    | undefined;

  const {
    background_color = "rgba(159, 28, 51, 0.20)",
    description_color = "#9F1C33",
    gradient_color_1 = "#9F1C33",
    gradient_color_4 = "rgba(159, 28, 51, 0.15)",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const items = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_description || r?.meta_icon))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || items.length === 0) return null;

  return (
    <section
      className="flex w-full items-center justify-center overflow-hidden tab:h-[140px]"
      style={{ backgroundColor: background_color }}
    >
      <div className={CONTAINER}>
        <div
          className={cn(
            "relative grid grid-cols-2",
            // v1 narrows the desktop row to the item count below four.
            items.length === 1 && "tab:grid-cols-1",
            items.length === 2 && "tab:grid-cols-2",
            items.length === 3 && "tab:grid-cols-3",
            items.length >= 4 && "tab:grid-cols-4",
          )}
        >
          {items.map((item, index) => (
            <div
              key={item.id ?? index}
              className={cn(
                "flex flex-col items-center justify-center gap-0 p-4",
                "border-b border-solid odd:border-r even:border-r-0",
                "tab:border tab:border-b-0 tab:py-6 tab:odd:border-r tab:even:border-r tab:last-of-type:border-r-0",
                index === 0 && "tab:border-l-0",
              )}
              style={{ borderColor: gradient_color_4 }}
            >
              {item.meta_icon ? (
                <>
                  <span className="hidden tab:block">
                    <DynamicIcon
                      iconName={item.meta_icon}
                      width={ICON_SIZE_DESKTOP}
                      height={ICON_SIZE_DESKTOP}
                      color={gradient_color_1}
                    />
                  </span>
                  <span className="tab:hidden">
                    <DynamicIcon
                      iconName={item.meta_icon}
                      width={ICON_SIZE_MOBILE}
                      height={ICON_SIZE_MOBILE}
                      color={gradient_color_1}
                    />
                  </span>
                </>
              ) : null}
              {item.meta_description ? (
                <p
                  className="mt-4 text-center text-sm font-bold tracking-wide tab:text-lg"
                  style={{ color: description_color }}
                >
                  {item.meta_description}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyNumbers5006;
